import type { ReactNode } from 'react';
import { Button } from '../Button/Button.tsx';

export interface Column<Row> {
  key: string;
  header: ReactNode;
  render: (row: Row) => ReactNode;
  /** Header becomes a sort button; sorting itself is done by the caller (usually server-side). */
  sortable?: boolean;
}

export interface SortState {
  key: string;
  direction: 'asc' | 'desc';
}

export interface TableProps<Row> {
  caption: string;
  columns: Array<Column<Row>>;
  rows: Row[];
  rowKey: (row: Row) => string;
  sort?: SortState | null;
  onSortChange?: (sort: SortState) => void;
  /** Cursor pagination: a "Load more" button while `hasMore`. */
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  empty?: ReactNode;
}

export function Table<Row>({ caption, columns, rows, rowKey, sort, onSortChange, hasMore, loadingMore, onLoadMore, empty }: TableProps<Row>) {
  if (rows.length === 0 && empty) return <>{empty}</>;
  const toggle = (key: string) => onSortChange?.({ key, direction: sort?.key === key && sort.direction === 'asc' ? 'desc' : 'asc' });
  return (
    <div>
      <table className="bb-table">
        <caption className="bb-visually-hidden">{caption}</caption>
        <thead>
          <tr>
            {columns.map((column) => {
              const sorted = sort?.key === column.key ? (sort.direction === 'asc' ? 'ascending' : 'descending') : undefined;
              return (
                <th key={column.key} scope="col" aria-sort={column.sortable ? (sorted ?? 'none') : undefined}>
                  {column.sortable ? (
                    <button type="button" className="bb-table__sort" onClick={() => toggle(column.key)}>
                      {column.header}
                      <span aria-hidden="true">{sorted === 'ascending' ? ' ▲' : sorted === 'descending' ? ' ▼' : ''}</span>
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={rowKey(row)}>
              {columns.map((column) => (
                <td key={column.key}>{column.render(row)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {hasMore && (
        <div className="bb-table__footer">
          <Button onClick={onLoadMore} loading={loadingMore}>
            Load more
          </Button>
        </div>
      )}
    </div>
  );
}
